import {
  Avatar,
  ButtonDS,
  ErrorOutlineOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  MessageTooltip,
  Spacer,
  TableTagLinkyConsentStatus,
  usePersistMuiTableState,
  useUrlTableFilter,
  triggerToastDS,
} from "@qivia/ui";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { formatDate } from "../../../libSlice/materialUiFormats";
import { selectCompany, selectManager } from "../homeSlice";
import {
  getLinkyConsentsAsync,
  linkyConsentsSlice,
  selectLinkyConsents,
  selectLinkyConsentsStatus,
  selectSendDriverOnboardingLinkStatus,
  sendDriverOnboardingLinkAsync,
} from "./linkyConsentsSlice";
import { LinkyConsentDisplayed, LinkyConsentStatus } from "./linkyConsentsAPI";
import { DriverSidePanel } from "./sidePanel/driverSidePanel/SidePanel";
import "../../../i18n/i18n";

const RESEND_LINK_COOLDOWN_MS = 30_000;

export const LinkyConnectionsTab = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const firstPath = location.pathname.split("/")[1];
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const linkyConsents = useAppSelector(selectLinkyConsents);
  const linkyConsentsStatus = useAppSelector(selectLinkyConsentsStatus);
  const sendDriverOnboardingLinkStatus = useAppSelector(
    selectSendDriverOnboardingLinkStatus,
  );
  const manager = useAppSelector(selectManager);
  const [listDisplayed, setListDisplayed] = useState<
    LinkyConsentDisplayed[] | null
  >(null);
  const [disabledDriverUuids, setDisabledDriverUuids] = useState<string[]>([]);

  const isOwnerOrAdmin = useMemo(
    () => manager?.role === "OWNER" || manager?.role === "ADMIN",
    [manager],
  );

  const pageName = "linky_consents";
  const apiRef = useGridApiRef();
  const dataGridState = usePersistMuiTableState(
    apiRef,
    "customers-grid",
    pageName,
  );

  useEffect(() => {
    if (company && linkyConsentsStatus === "idle") {
      void dispatch(getLinkyConsentsAsync(company.uuid));
    }
  }, [company, dispatch, linkyConsentsStatus]);

  const allConsentsDisplayed: LinkyConsentDisplayed[] = useMemo(() => {
    return linkyConsents.map((consent) => {
      const row: LinkyConsentDisplayed & { id: string } = {
        driverName: `${consent.firstName} ${consent.lastName}`,
        consentStatus: consent.consentStatus,
        connectionDate: consent.connectionDate,
        consentLinkExpiresAt: consent.consentLinkExpiresAt,
        id: consent.uuid,
      };
      return row;
    });
  }, [linkyConsents]);

  useEffect(() => {
    if (linkyConsentsStatus === "success") {
      setListDisplayed(allConsentsDisplayed);
    }
  }, [allConsentsDisplayed, linkyConsentsStatus]);

  const onResend = useCallback(
    (driverUuid: string) => {
      setDisabledDriverUuids((uuids) => [...uuids, driverUuid]);
      setTimeout(
        () =>
          setDisabledDriverUuids((uuids) =>
            uuids.filter((uuid) => uuid !== driverUuid),
          ),
        RESEND_LINK_COOLDOWN_MS,
      );
      void dispatch(sendDriverOnboardingLinkAsync(driverUuid));
    },
    [dispatch],
  );

  useEffect(() => {
    if (sendDriverOnboardingLinkStatus === "success") {
      triggerToastDS(t("linky_consents.action.resend.success"), "SUCCESS");
      dispatch(
        linkyConsentsSlice.actions.resetSendDriverOnboardingLinkStatus(),
      );
    } else if (sendDriverOnboardingLinkStatus === "failed") {
      triggerToastDS(t("linky_consents.action.resend.error"), "ERROR");
      dispatch(
        linkyConsentsSlice.actions.resetSendDriverOnboardingLinkStatus(),
      );
    }
  }, [sendDriverOnboardingLinkStatus, t, dispatch]);

  const [searchParams] = useSearchParams();

  useUrlTableFilter(apiRef, !!listDisplayed, {
    value: searchParams.get("filterStatus"),
    field: "consentStatus",
    filterId: "linkyStatusUrlFilter",
  });

  const searchBarProps = {
    values: allConsentsDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["id" as const, "consentLinkExpiresAt" as const],
  };

  const columnsDisplayed: {
    [index in keyof LinkyConsentDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      driverName: {
        type: "string",
        width: 250,
        valueFormatter: (value: string) => {
          if (!value) return <>-</>;
          const initials = value.split(" ").map((word) => word.charAt(0));
          return (
            <StyledRow>
              <Avatar
                color={"mint"}
                size={"S"}
                firstName={initials[0]}
                lastName={initials[initials.length - 1]}
              />
              <Spacer x={0.75} />
              {value}
            </StyledRow>
          );
        },
      },
      consentStatus: {
        type: "string",
        width: 200,
        renderCell: (p) => (
          <StyledCell>
            <TableTagLinkyConsentStatus status={p.value as string} />
          </StyledCell>
        ),
      },
      connectionDate: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => (value ? formatDate(value) : "-"),
      },
      consentLinkExpiresAt: {
        type: "string",
        width: 200,
        renderCell: (p) => {
          const status = p.row.consentStatus as LinkyConsentStatus;
          const expiresAt = p.row.consentLinkExpiresAt as string | null;
          const driverUuid = p.id as string;
          if (!expiresAt || status === "VALIDATED" || !isOwnerOrAdmin) {
            return <>-</>;
          }
          const isConsentLinkNotExpired = new Date(expiresAt) > new Date();
          return (
            <StyledCell onClick={(event) => event.stopPropagation()}>
              <ButtonDS
                text={t("linky_consents.action.resend")}
                buttonType={"secondary"}
                format={"hug"}
                onClick={() => onResend(driverUuid)}
                disabled={
                  isConsentLinkNotExpired ||
                  disabledDriverUuids.includes(driverUuid)
                }
              />
            </StyledCell>
          );
        },
      },
    };
  }, [t, isOwnerOrAdmin, disabledDriverUuids, onResend]);

  const openSidePanelAction = useCallback(
    (row: Partial<LinkyConsentDisplayed> & { id?: string }) => {
      navigate(`/${firstPath}/electric_charging/linky_connections/${row.id}`);
    },
    [navigate, firstPath],
  );

  if (params.tab !== "linky_connections") {
    return null;
  }

  return (
    <StyledContainer>
      <MessageTooltip
        type={"NEUTRAL"}
        iconLeft={<ErrorOutlineOutlined />}
        title={t("linky_consents.banner")}
      />
      <Spacer y={2} />
      {!listDisplayed ? (
        <LaunchPage hasBorderRadius={true} />
      ) : (
        <MaterialUiTable<keyof LinkyConsentDisplayed, LinkyConsentDisplayed>
          rows={listDisplayed}
          columns={columnsDisplayed}
          pageName={pageName}
          dataGridState={dataGridState}
          searchBar={searchBarProps}
          apiRef={apiRef}
          onRowClickAction={openSidePanelAction}
        />
      )}
      <DriverSidePanel />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StyledCell = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
